// ============================================
// App.tsx - MARKFED Maize MSP Portal — Complete Routing
// ============================================
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Auth & Layout
import MarkfedAuthProvider from './contexts/AuthContext';
import AdminLayout from './components/admin/AdminLayout';
import ExternalLayout from './components/external/ExternalLayout';
import MarkfedLogin from './pages/Login';
import MarkfedDashboard from './pages/dashboard/MarkfedDashboard';
import ChangePassword from './pages/ChangePassword';
import SocietyDashboard from './pages/society/SocietyDashboard';
import SocietyIncoming from './pages/society/SocietyIncoming';
import SocietyRequests from './pages/society/SocietyRequests';
import SocietyTransfers from './pages/society/SocietyTransfers';
import SupplierDashboard from './pages/supplier/SupplierDashboard';
import SupplierClaims from './pages/supplier/SupplierClaims';
import { Truck, ArrowRightLeft, Inbox, Package, FileText, ClipboardList } from 'lucide-react';
import ManagerProcurementDashboard from './pages/manager-procurement/ManagerProcurementDashboard';
import CaoDashboard from './pages/cao/CaoDashboard';
import { UserRole } from './types/markfed';

// Maize MSP — Data Entry
import LoanSanctionForm from './pages/data-entry/LoanSanctionForm';
import DrawdownsForm from './pages/data-entry/DrawdownsForm';
import UtilizationList from './pages/data-entry/UtilizationList';
import UtilizationForm from './pages/data-entry/UtilizationForm';
import FarmersList from './pages/data-entry/FarmersList';
import FarmersForm from './pages/data-entry/FarmersForm';

// Maize MSP — Reports
import MDSheetView from './pages/reports/MDSheetView';
import FarmersSheetView from './pages/reports/FarmersSheetView';

// Admin Pages
import UserManagement from './pages/admin/UserManagement';
import DistrictManagement from './pages/admin/DistrictManagement';
import PACSManagement from './pages/admin/PACSManagement';
import SeasonManagement from './pages/admin/SeasonManagement';
import BanksManagement from './pages/admin/BanksManagement';
import UtilizationHeadsManagement from './pages/admin/UtilizationHeadsManagement';
import ApprovalQueue from './pages/admin/ApprovalQueue';

// Transportation Module
import { TransportationDashboard } from './pages/transportation/TransportationDashboard';
import { VehiclesMaster } from './pages/transportation/VehiclesMaster';
import { AddVehicleForm } from './pages/transportation/AddVehicleForm';

// Gunny Bags Module
import { GunnyBagsDashboard } from './pages/gunny-bags/GunnyBagsDashboard';
import { CreatePOForm } from './pages/gunny-bags/CreatePOForm';
import { GunnyPosList } from './pages/gunny-bags/GunnyPosList';
import GunnyRequestsManagement from './pages/gunny-bags/GunnyRequestsManagement';
import GunnyTransfersManagement from './pages/gunny-bags/GunnyTransfersManagement';

// Storage Module
import { StorageDashboard } from './pages/storage/StorageDashboard';
import { StorageSummary } from './pages/storage/StorageSummary';
import { GenerateStorageBillForm } from './pages/storage/GenerateStorageBillForm';

// Release Orders Module
import { ReleaseOrdersDashboard } from './pages/release-orders/ReleaseOrdersDashboard';
import { GenerateROForm } from './pages/release-orders/GenerateROForm';

// Admin — New Master Data Pages
import { CommoditiesManagement } from './pages/admin/CommoditiesManagement';
import { SeasonCommoditiesManagement } from './pages/admin/SeasonCommoditiesManagement';
import { GodownsManagement } from './pages/admin/GodownsManagement';
import { ContractorsManagement } from './pages/admin/ContractorsManagement';
import { UnloadingContractorsManagement } from './pages/admin/UnloadingContractorsManagement';
import { GodownContractorsManagement } from './pages/admin/GodownContractorsManagement';
import { GunnySuppliersManagement } from './pages/admin/GunnySuppliersManagement';

// Procurement Module
import { ProcurementCentresList } from './pages/procurement/ProcurementCentresList';
import { DistrictProductionList } from './pages/procurement/DistrictProductionList';
import { DistrictProductionForm } from './pages/procurement/DistrictProductionForm';
import { ProcurementTrackingList } from './pages/procurement/ProcurementTrackingList';
import { ProcurementTrackingForm } from './pages/procurement/ProcurementTrackingForm';

// Unloading Bills Module
import { UnloadingBillsList } from './pages/unloading/UnloadingBillsList';
import { UnloadingBillForm } from './pages/unloading/UnloadingBillForm';
import { UnloadingEntriesList } from './pages/unloading/UnloadingEntriesList';
import { UnloadingEntryForm } from './pages/unloading/UnloadingEntryForm';
import { UnloadingPaymentsLedger } from './pages/unloading/UnloadingPaymentsLedger';
import { UnloadingDashboard } from './pages/unloading/UnloadingDashboard';

// Transport — Real API Pages
import { TransportTripsList } from './pages/transportation/TransportTripsList';
import { TransportTripForm } from './pages/transportation/TransportTripForm';
import { TransportBillsList } from './pages/transportation/TransportBillsList';
import { TransportBillForm } from './pages/transportation/TransportBillForm';
import { TransportSlabsPage } from './pages/transportation/TransportSlabsPage';

// Gunny — Real API Pages
import { GunnyUtilizationList } from './pages/gunny-bags/GunnyUtilizationList';
import { GunnyUtilizationForm } from './pages/gunny-bags/GunnyUtilizationForm';
import { GunnyInventoryReport } from './pages/gunny-bags/GunnyInventoryReport';
import { GunnySupplierPerformance } from './pages/gunny-bags/GunnySupplierPerformance';
import { GunnyReconciliationReport } from './pages/gunny-bags/GunnyReconciliationReport';
import { TransporterPaymentsLedger } from './pages/transportation/TransporterPaymentsLedger';
import { ROPaymentsLedger } from './pages/release-orders/ROPaymentsLedger';

// Reports — New Pages
import { DPRReport } from './pages/reports/DPRReport';
import { ReconciliationDashboard } from './pages/reports/ReconciliationDashboard';
import { HistoricalProcurement } from './pages/reports/HistoricalProcurement';
import { ImportExport } from './pages/reports/ImportExport';
import { OperationalProformas } from './pages/reports/OperationalProformas';
import { ReportsHub } from './pages/reports/ReportsHub';
import { UtilizationReconciliationPage } from './pages/reports/UtilizationReconciliationPage';
import { PayablesLedgerPage } from './pages/reports/PayablesLedgerPage';

function App() {
  return (
    <BrowserRouter>
      <MarkfedAuthProvider>
        <Routes>
          {/* Login / first-login password change */}
          <Route path="/login" element={<MarkfedLogin />} />
          <Route path="/change-password" element={<ChangePassword />} />

          {/* External actor portals — lightweight shell with sidebar, role-gated */}
          <Route
            path="/society"
            element={<ExternalLayout
              allowed={[UserRole.SOCIETY]}
              title="Society Portal"
              accent="green"
              nav={[
                { to: '/society/incoming', label: 'Incoming Lots', icon: <Truck className="w-4 h-4" /> },
                { to: '/society/requests', label: 'Gunny Requests', icon: <Inbox className="w-4 h-4" /> },
                { to: '/society/consignments', label: 'My Consignments', icon: <Package className="w-4 h-4" />, end: true },
                { to: '/society/transfers', label: 'Transfers', icon: <ArrowRightLeft className="w-4 h-4" /> },
              ]}
            />}
          >
            <Route index element={<Navigate to="/society/consignments" replace />} />
            <Route path="incoming" element={<SocietyIncoming />} />
            <Route path="requests" element={<SocietyRequests />} />
            <Route path="consignments" element={<SocietyDashboard />} />
            <Route path="transfers" element={<SocietyTransfers />} />
          </Route>
          <Route
            path="/supplier"
            element={<ExternalLayout
              allowed={[UserRole.SUPPLIER]}
              title="Supplier Portal"
              accent="amber"
              nav={[
                { to: '/supplier/orders', label: 'Purchase Orders', icon: <ClipboardList className="w-4 h-4" /> },
                { to: '/supplier/claims', label: 'Bill Claims', icon: <FileText className="w-4 h-4" /> },
              ]}
            />}
          >
            <Route index element={<Navigate to="/supplier/orders" replace />} />
            <Route path="orders" element={<SupplierDashboard />} />
            <Route path="claims" element={<SupplierClaims />} />
          </Route>

          {/* Protected Routes */}
          <Route path="/" element={<AdminLayout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />

            {/* Dashboard */}
            <Route path="dashboard" element={<MarkfedDashboard />} />

            {/* Manager-Procurement & CAO portals (internal roles, full sidebar) */}
            <Route path="manager-procurement" element={<ManagerProcurementDashboard />} />
            <Route path="cao" element={<CaoDashboard />} />

            {/* ==================== MAIZE MSP DATA ENTRY ==================== */}
            <Route path="data-entry">
              <Route path="loan" element={<LoanSanctionForm />} />
              <Route path="drawdowns" element={<DrawdownsForm />} />
              <Route path="utilization" element={<UtilizationList />} />
              <Route path="utilization/new" element={<UtilizationForm />} />
              <Route path="utilization/:id" element={<UtilizationForm />} />
              <Route path="farmers" element={<FarmersList />} />
              <Route path="farmers/new" element={<FarmersForm />} />
              <Route path="farmers/:id" element={<FarmersForm />} />
            </Route>

            {/* ==================== REPORTS ==================== */}
            <Route path="reports">
              <Route index element={<ReportsHub />} />
              <Route path="md-sheet" element={<MDSheetView />} />
              <Route path="farmers-sheet" element={<FarmersSheetView />} />
              <Route path="dpr" element={<DPRReport />} />
              <Route path="proformas" element={<OperationalProformas />} />
              <Route path="reconciliation" element={<ReconciliationDashboard />} />
              <Route path="utilization-reconciliation" element={<UtilizationReconciliationPage />} />
              <Route path="payables" element={<PayablesLedgerPage />} />
              <Route path="historical" element={<HistoricalProcurement />} />
              <Route path="import-export" element={<ImportExport />} />
            </Route>

            {/* ==================== ADMIN (SUPER_ADMIN) ==================== */}
            <Route path="admin">
              <Route path="users" element={<UserManagement />} />
              <Route path="districts" element={<DistrictManagement />} />
              <Route path="pacs" element={<PACSManagement />} />
              <Route path="seasons" element={<SeasonManagement />} />
              <Route path="banks" element={<BanksManagement />} />
              <Route path="utilization-heads" element={<UtilizationHeadsManagement />} />
              <Route path="approvals" element={<ApprovalQueue />} />
              <Route path="commodities" element={<CommoditiesManagement />} />
              <Route path="season-commodities" element={<SeasonCommoditiesManagement />} />
              <Route path="godowns" element={<GodownsManagement />} />
              <Route path="contractors" element={<ContractorsManagement />} />
              <Route path="unloading-contractors" element={<UnloadingContractorsManagement />} />
              <Route path="godown-contractors" element={<GodownContractorsManagement />} />
              <Route path="gunny-suppliers" element={<GunnySuppliersManagement />} />
            </Route>

            {/* ==================== PROCUREMENT MODULE ==================== */}
            <Route path="procurement">
              <Route path="centres" element={<ProcurementCentresList />} />
              <Route path="production" element={<DistrictProductionList />} />
              <Route path="production/edit/:scId/:districtId" element={<DistrictProductionForm />} />
              <Route path="tracking" element={<ProcurementTrackingList />} />
              <Route path="tracking/:scId/new" element={<ProcurementTrackingForm />} />
              <Route path="tracking/:scId/:id" element={<ProcurementTrackingForm />} />
            </Route>

            {/* ==================== UNLOADING BILLS MODULE ==================== */}
            <Route path="unloading">
              <Route index element={<UnloadingBillsList />} />
              <Route path="bills/new" element={<UnloadingBillForm />} />
              <Route path="bills/:id" element={<UnloadingBillForm />} />
              <Route path="entries" element={<UnloadingEntriesList />} />
              <Route path="entries/new" element={<UnloadingEntryForm />} />
              <Route path="entries/:id" element={<UnloadingEntryForm />} />
              <Route path="payments" element={<UnloadingPaymentsLedger />} />
              <Route path="dashboard" element={<UnloadingDashboard />} />
            </Route>

            {/* ==================== TRANSPORTATION MODULE ==================== */}
            <Route path="transportation">
              <Route index element={<TransportationDashboard />} />
              <Route path="vehicles">
                <Route index element={<VehiclesMaster />} />
                <Route path="add" element={<AddVehicleForm />} />
                <Route path="edit/:id" element={<AddVehicleForm />} />
              </Route>
              {/* Transporters master is the same as admin/contractors — redirect */}
              <Route path="transporters" element={<Navigate to="/admin/contractors" replace />} />
              <Route path="transporters/*" element={<Navigate to="/admin/contractors" replace />} />
              <Route path="slabs" element={<TransportSlabsPage />} />
              <Route path="trips" element={<TransportTripsList />} />
              <Route path="trips/new" element={<TransportTripForm />} />
              <Route path="trips/:id" element={<TransportTripForm />} />
              <Route path="billing" element={<TransportBillsList />} />
              <Route path="billing/new" element={<TransportBillForm />} />
              <Route path="billing/:id" element={<TransportBillForm />} />
              <Route path="payments" element={<TransporterPaymentsLedger />} />
            </Route>

            {/* ==================== GUNNY BAGS MODULE ==================== */}
            <Route path="gunny-bags">
              <Route index element={<GunnyBagsDashboard />} />
              {/* Vendors master is admin/gunny-suppliers — redirect */}
              <Route path="vendors" element={<Navigate to="/admin/gunny-suppliers" replace />} />
              <Route path="vendors/*" element={<Navigate to="/admin/gunny-suppliers" replace />} />
              {/* GRN is captured inside GunnyUtilization (supplier_invoice + bags_supplied) — redirect */}
              <Route path="grn" element={<Navigate to="/gunny-bags/utilization" replace />} />
              {/* Issuance is the supplier→society flow inside GunnyUtilization — redirect */}
              <Route path="issuance" element={<Navigate to="/gunny-bags/utilization" replace />} />
              <Route path="procurement">
                <Route index element={<GunnyPosList />} />
                <Route path="create" element={<CreatePOForm />} />
                <Route path="edit/:id" element={<CreatePOForm />} />
              </Route>
              <Route path="requests" element={<GunnyRequestsManagement />} />
              <Route path="transfers" element={<GunnyTransfersManagement />} />
              <Route path="inventory" element={<GunnyInventoryReport />} />
              <Route path="performance" element={<GunnySupplierPerformance />} />
              <Route path="utilization" element={<GunnyUtilizationList />} />
              <Route path="utilization/new" element={<GunnyUtilizationForm />} />
              <Route path="utilization/:id" element={<GunnyUtilizationForm />} />
              <Route path="reconciliation" element={<GunnyReconciliationReport />} />
            </Route>

            {/* ==================== STORAGE MODULE ==================== */}
            <Route path="storage">
              <Route index element={<StorageSummary />} />
              <Route path="bills">
                <Route index element={<StorageDashboard />} />
                <Route path="create" element={<GenerateStorageBillForm />} />
                <Route path="edit/:id" element={<GenerateStorageBillForm />} />
              </Route>
              {/* Backward-compat: /storage/billing/* → /storage/bills/* */}
              <Route path="billing" element={<Navigate to="/storage/bills" replace />} />
              <Route path="billing/create" element={<Navigate to="/storage/bills/create" replace />} />
              <Route path="billing/edit/:id" element={<GenerateStorageBillForm />} />
            </Route>

            {/* ==================== RELEASE ORDERS MODULE ==================== */}
            <Route path="release-orders">
              <Route index element={<ReleaseOrdersDashboard />} />
              <Route path="generate" element={<GenerateROForm />} />
              <Route path="edit/:id" element={<GenerateROForm />} />
              <Route path="payments" element={<ROPaymentsLedger />} />
              {/* Removed 7 dead sub-routes (amendments/lifting/delivery/weighment/gate-pass/
                  closure/analytics) — would need their own backend modules to be real. */}
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </MarkfedAuthProvider>
    </BrowserRouter>
  );
}

export default App;
